// The German catalogue for the server-rendered public pages.
//
// German is the reference: `en.js` is asserted against these keys in both
// directions, so a key added here without a translation fails the suite rather
// than shipping an English page with a German sentence in it.
//
// Copy that varies with data is a function taking values, never a pre-built
// sentence assembled at the call site. That is what lets "jeden letzten
// Donnerstag im Monat" and "every last Thursday of the month" differ in
// grammar without forking the rule logic — and why the count goes in as a
// number rather than as text.
//
// Publisher content is never in here. Titles, descriptions and tags stay
// exactly as a venue typed them; a machine translation of a club's own words
// is worse than the German original, and the taxonomy is content too.

// A date written out, not "2026-09-15": a date read aloud digit by digit is
// the kind of thing that makes a screen reader user give up on a calendar.
// Through Intl, so the two languages differ where they should — "12. August
// 2026" against "12 August 2026" — without either spelling out month names.
const LONG_DATE = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
});

function longDate(iso) {
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? String(iso) : LONG_DATE.format(date);
}

// "a, b und c" — through Intl rather than a join, because the conjunction and
// the comma before it differ between the two languages.
const LIST = new Intl.ListFormat('de-DE', { style: 'long', type: 'conjunction' });

import legal from './legal-de.js';
import mail from './mail-de.js';

export default {
  // Whole documents rather than chrome, so the prose lives in its own file —
  // spread in flat, so the key-parity test covers it without knowing that.
  ...legal,
  // Everything this product sends. Beside the catalogue for the same reason
  // the legal pages are: prose rather than chrome, spread in flat so the
  // key-parity test covers it without knowing it is special.
  ...mail,

  // chrome
  brandMark: 'tba',
  brandName: 'tips',
  navExplore: 'Entdecken',
  navFeed: 'Folge ich',
  navSaved: 'Gemerkt',
  panelTitle: 'Panel',
  navDates: 'Termine',
  navPage: 'Seite',
  navAccountSettings: 'Konto',
  navUsers: 'Nutzer',
  navStats: 'Statistik',
  navCrawl: 'Crawl',
  backLabel: 'Zurück',
  // The quiet shortcut on the public event page. It has no visible word — the
  // label is its whole accessible name and its tooltip.
  editInPanel: 'Im Panel bearbeiten',
  navBackend: 'Panel',
  navAccount: 'Konto',
  navSignIn: 'Anmelden',
  themeToggle: 'Zwischen hell und dunkel wechseln',
  switchLanguage: 'English',
  switchLanguageLabel: 'Switch to English',

  navMyPage: 'Seite',

  // profile
  profileTitle: 'Öffentliches Profil',
  profileLede: 'Das sehen Besucher auf deiner Seite. Nichts davon ist Pflicht, und veröffentlicht wird die Seite erst, wenn du es unten sagst.',
  fieldNickname: 'Kurzname',
  nicknameHint: 'Deine Adresse lautet dann tba.tips/u/kurzname. Kleinbuchstaben, Ziffern und Bindestriche, mindestens drei Zeichen.',
  fieldDisplayName: 'Anzeigename',
  fieldAbout: 'Über dich',
  fieldContact: 'Kontakt',
  contactHint: 'Steht öffentlich auf deiner Seite. Nicht deine Anmelde-Adresse — die bleibt privat.',
  fieldInstagram: 'Instagram',
  fieldSoundcloud: 'SoundCloud',
  fieldBandcamp: 'Bandcamp',
  fieldSocials: 'Social',
  publicPageTitle: 'Deine Seite',
  publicPageLede: 'Deine Seite ist nicht veröffentlicht, bis du es hier sagst. Sie zeigt, was du machst — und das ist deine Entscheidung, nicht unsere.',
  pagePublish: 'Seite veröffentlichen',
  pageUnpublish: 'Seite offline nehmen',
  pageNeedsNickname: 'Trag oben einen Kurznamen ein, dann hat deine Seite eine Adresse.',
  nickname_format: 'Der Kurzname darf nur Kleinbuchstaben, Ziffern und einzelne Bindestriche enthalten.',
  nickname_too_short: 'Der Kurzname ist zu kurz.',
  nickname_too_long: 'Der Kurzname ist zu lang.',
  nickname_taken: 'Dieser Kurzname ist schon vergeben.',
  nickname_reserved: 'Dieser Kurzname geht nicht.',

  // Just the word. The product name was redundant in a footer that sits under
  // the wordmark on every page, and it was the widest of the five links on the
  // row that runs out of width first.
  footerAbout: 'Über',
  dayEventCount: (n) => (n === 1 ? '1 Termin' : `${n} Termine`),
  footerLegal: 'Impressum & Datenschutz',
  footerReport: 'Melden',
  // Die Datenlizenz im Footer. Kurz, weil sie neben vier anderen Links steht —
  // die Erklärung steht in LICENSING.md, hier steht nur, dass es eine gibt.
  footerData: 'Daten: ODbL',

  // filters
  search: 'Suchen',
  filterApply: 'Filtern',
  filterReset: 'Zurücksetzen',
  // The name of the whole band, which is what the two handles are grouped
  // under. Two sliders with no group label are two anonymous numbers.
  filterTime: 'Zeitraum',
  // The two ends of the range, both inclusive months, and the one date
  // question no range expresses.
  filterFrom: 'Von',
  filterTo: 'Bis',
  filterWeekends: 'Wochenenden',
  filterToday: 'Heute',
  // The selection written out is no longer a key: `Intl.DateTimeFormat`
  // punctuates a span itself, per language, and it drops what the two ends share
  // — "24.–30. Sept. 26" — which a template with two holes in it cannot. See
  // formatDateRange in server/lib/i18n.js. The resting word below stays here,
  // because that one is copy rather than a date.
  filterCity: 'Stadt',
  filterTag: 'Tags',
  filterGenre: 'Genre',

  // The facet vocabulary. Facets are chrome we curate, so they are translated;
  // tags are what publishers write, so they are not. `taxonomy.name_key` points
  // at these keys, and a key that is missing here falls back to itself — a
  // moderator who adds a facet from the dashboard cannot also ship a locale.

  // The fourth domain, for everything the first three do not describe. No
  // styles under it: a flea market and a panel share no axis a style could
  // name, and inventing one would be a vocabulary nobody could fill in.
  facetOther: 'Sonstiges',
  facetTalk: 'Vortrag',
  facetWorkshop: 'Workshop',
  facetPanel: 'Panel-Talk',
  facetReading: 'Lesung',
  facetScreening: 'Film',
  facetConference: 'Konferenz',
  facetFleaMarket: 'Flohmarkt',
  facetDemo: 'Demo',
  facetActivity: 'Aktivität',

  facetMusic: 'Musik',
  facetArt: 'Kunst',
  facetFood: 'Essen',
  facetConcert: 'Konzert',
  facetDj: 'DJ',
  facetClubbing: 'Clubbing',
  facetParty: 'Party',
  facetRave: 'Rave',
  facetLive: 'Live',
  facetListening: 'Listening',
  facetExhibition: 'Ausstellung',
  facetVernissage: 'Vernissage',
  facetFinissage: 'Finissage',
  facetStreetfood: 'Streetfood',
  facetDrinks: 'Getränke',
  facetRestaurant: 'Restaurant',
  facetPopup: 'Pop-up',
  facetSupperClub: 'Supper Club',
  facetTechno: 'Techno',
  facetHouse: 'House',
  facetDisco: 'Disco',
  facetAmbient: 'Ambient',
  facetDrumAndBass: 'Drum & Bass',
  facetDub: 'Dub',
  facetJazz: 'Jazz',
  facetPunk: 'Punk',
  facetIndie: 'Indie',
  facetHiphop: 'HipHop',
  facetExperimental: 'Experimentell',
  facetClassical: 'Klassik',
  facetElectro: 'Electro',
  facetTrance: 'Trance',
  facetPop: 'Pop',
  facetPerformance: 'Performance',
  facetPainting: 'Malerei',
  facetSculpture: 'Skulptur',
  facetFilm: 'Film',
  facetPhotography: 'Fotografie',
  facetInstallation: 'Installation',
  facetDrawing: 'Zeichnung',
  facetPrint: 'Grafik',
  facetMediaArt: 'Medienkunst',
  facetSoundArt: 'Klangkunst',
  facetGuidedTour: 'Führung',
  facetOpenStudio: 'Offenes Atelier',
  facetAsian: 'Asiatisch',
  facetMediterranean: 'Mediterran',
  facetFusion: 'Fusion',
  facetDeftig: 'Deftig',
  facetVegan: 'Vegan',
  facetIndoor: 'Drinnen',
  facetOutdoor: 'Draußen',

  allDates: 'Alle Termine',
  allCities: 'Alle Städte',
  allTags: 'Alle Tags',
  filterFree: 'Kostenlos',
  filterAnySetting: 'Überall',
  // The cycling control's whole accessible name: where it stands, then what a
  // click does. The visible label is only the first half, because the second
  // half is what the arrow shape is for.
  settingCycle: (current, next) => `Drinnen oder draußen: ${current}. Weiter zu ${next}`,
  fieldPriceFree: 'Eintritt frei',

  // The moderator's vocabulary screen. "The rows are data, not an enum" was
  // true of the schema from the first migration; this is where it becomes true
  // of the product.
  vocabulary: 'Vokabular',
  vocabularyHint: 'Bereiche, Formate, Stile und das Setting. Neue Einträge sind sofort im Editor und im Filter — es gibt keinen Deploy dazwischen.',
  vocabularyDelete: 'Löschen',
  vocabularyAdd: 'Hinzufügen',
  vocabularyName: 'Name',
  vocabularySlug: 'Kurzform (optional)',
  vocabularyKind: 'Art',
  vocabularyParent: 'Bereich',
  vocabularyUsed: count => count === 1 ? '1 Reihe' : `${count} Reihen`,
  vocabularyDeleteBlocked: 'Wird benutzt — erst umhängen, dann löschen.',
  kindDomain: 'Bereich',
  kindFormat: 'Format',
  kindStyle: 'Stil',
  kindSetting: 'Setting',

  allDomains: 'Alle Bereiche',
  allFormats: 'Alle Formate',
  allStyles: 'Alle Stile',
  allSettings: 'Drinnen und draußen',
  allGenres: 'Alle Genres',
  timeToday: 'Heute',
  timeTomorrow: 'Morgen',
  timeWeekend: 'Wochenende',
  timeNext7: 'Nächste 7 Tage',

  // Die Namen der schema.org-Listen. Kein Mensch liest sie, ein Agent schon —
  // und wenn die Seite auf Englisch steht, muss auch der Listenname es tun,
  // sonst widerspricht das `inLanguage` daneben sich selbst.
  jsonLdFeedList: 'Veranstaltungen im Rheinland',
  jsonLdCityList: (city) => `Veranstaltungen in ${city}`,
  jsonLdVenueList: (name) => `Programm von ${name}`,

  viewLabel: 'Ansicht',
  viewList: 'Liste',
  viewCalendar: 'Kalender',
  viewMap: 'Karte',
  previousMonth: 'Voriger Monat',
  nextMonth: 'Nächster Monat',

  // Karte. Die Namensnennung ist eine Lizenzbedingung der ODbL, keine
  // Höflichkeit — sie steht auf jeder Seite, die einen Umriss zeichnet.
  mapAttribution: 'Kartengrundlage © OpenStreetMap-Mitwirkende und Protomaps, ODbL',
  mapEmpty: 'Für diesen Tag ist nichts mit Adresse eingetragen.',
  mapDayLabel: 'Tag wählen',
  mapDayPrev: 'Voriger Tag',
  mapDayNext: 'Nächster Tag',
  mapCityLabel: (city) => `Stadtplan von ${city}`,
  mapRegionLabel: 'Übersichtskarte der ganzen Region',
  mapCityLink: (city) => `${city} genauer ansehen`,
  mapWholeRegion: 'Ganze Region',
  mapZoomIn: 'Näher heran',
  mapZoomOut: 'Weiter weg',
  mapZoomReset: 'Ansicht zurücksetzen',

  // buckets
  bucketToday: 'Heute',
  bucketTomorrow: 'Morgen',
  bucketWeekend: 'Wochenende',
  bucketWeek: 'Diese Woche',
  bucketLater: 'Später',
  bucketPast: 'Vorbei',

  // cards
  bookmark: 'Merken',
  bookmarked: 'Gemerkt',
  saveCount: (count) => `${count}`,
  follow: 'Folgen',
  following: 'Du folgst',
  cancelledNotice: 'Dieser Termin fällt aus.',
  tickets: 'Tickets',
  moreLinks: 'Weitere Links',
  postedBy: 'Eingetragen von',
  address: 'Adresse',
  price: 'Eintritt',
  subscribeCalendar: 'Kalender abonnieren',

  // Teilen-Sheet und Lightbox (M5). Das vollständige Sheet steht nur auf der
  // Event-Seite; in Listen ist der Knopf nichts als ein Link auf diesen Anker.
  share: 'Teilen',
  shareSheetTitle: 'Teilen & zum Kalender',
  shareEvent: (title) => `„${title}“ teilen`,
  addToCalendar: 'Zum Kalender hinzufügen',
  calendarGoogle: 'Google Calendar',
  calendarOutlook: 'Outlook',
  subscribeVenue: (name) => `Alle Termine von ${name} abonnieren`,
  sendTo: 'Weitersagen',
  shareWhatsapp: 'WhatsApp',
  shareTelegram: 'Telegram',
  shareEmail: 'E-Mail',
  shareNative: 'Teilen …',
  enlargeImage: 'Bild vergrößern',

  // Ein Ort, dessen Programm tipsBOT aus öffentlichen Quellen pflegt. Steht auf
  // der Seite, weil wir nie eine öffentliche Identität veröffentlichen, die der
  // Ort nicht gesehen hat — und weil der Hinweis der Grund ist, sie zu übernehmen.
  botMaintained: 'Dieses Programm pflegt tipsBOT aus öffentlichen Quellen — nicht die Institution selbst.',
  botMaintainedClaim: 'Gehört das dir? Übernimm die Seite.',

  // Steht auf jeder Seite, die noch niemand aus dem Laden übernommen hat — ob
  // tipsBOT sie pflegt oder ein Mensch aus der Redaktion. Solange sie
  // unbestätigt ist, bleibt das Aussehen neutral: eine gestaltete Identität für
  // einen Laden, der sie nie gesehen hat, veröffentlichen wir nicht.
  unclaimedPage: 'Diese Seite hat noch niemand aus der Institution übernommen.',

  // empty states
  emptyFiltered: 'Dazu ist nichts eingetragen.',
  emptyFilteredAction: 'Filter zurücksetzen',
  // Names the *range* as the empty thing, not the filters: the city and the
  // genre beside it are still perfectly good. The link does the widening,
  // because "drag a handle back" is an instruction rather than a way out.
  emptyRange: 'In diesem Zeitraum ist nichts eingetragen.',
  emptyRangeAction: 'Ganzes Jahr zeigen',
  emptyFeed: 'Noch nichts eingetragen. Wenn du eine Institution betreibst:',
  emptyFeedAction: 'trag dein Programm ein',
  emptyVenue: 'Aktuell keine Termine eingetragen.',

  // /meine
  feedTitle: 'Folge ich',
  feedEmpty: 'Du folgst noch niemandem. Auf jeder Seite gibt es einen Folgen-Knopf — danach steht hier nur noch deren Programm.',
  feedQuiet: 'Wem du folgst, hat gerade nichts angekündigt.',

  // /gemerkt
  subscribeSaved: 'Gemerktes abonnieren',
  subscribeSavedHint: 'Ein Kalender-Abo, das jede neue Merkung von selbst übernimmt. Die Adresse entsteht erst, wenn du sie anforderst.',
  subscribeSavedLive: 'Es gibt eine Abo-Adresse. Neu erzeugen macht die alte sofort ungültig.',
  subscribeCreate: 'Abo-Adresse erzeugen',
  subscribeRotate: 'Neu erzeugen',
  subscribeRevoke: 'Abo beenden',
  subscribeOpen: 'Im Kalender öffnen',
  subscribeOnce: 'Diese Adresse siehst du nur jetzt. Sie wird nicht gespeichert — verlierst du sie, erzeugst du eine neue.',
  subscribeWarning: 'Wer diese Adresse hat, sieht, auf welchen Abenden du sein willst. Gib sie nicht weiter, und erzeuge sie neu, wenn das doch passiert ist.',
  backToSaved: 'Zurück zu Gemerkt',
  archiveTitle: 'Archiv',
  archiveLede: 'Alles, was schon war. Neueste zuerst.',
  archiveEmpty: 'Zu dieser Auswahl liegt nichts Vergangenes vor.',
  pagerNewer: 'Neuere',
  pagerOlder: 'Ältere',
  // Not the archive's two words: there the list runs backwards and *Ältere*
  // points forward. The feed runs forwards, so the same button would name the
  // wrong direction.
  pagerMore: 'Weitere Termine',
  pagerBack: 'Vorherige Termine',
  pagerAdded: (count) => (count === 1
    ? 'Ein weiterer Termin geladen.'
    : `${count} weitere Termine geladen.`),
  emptyPage: 'Hier ist die Liste zu Ende.',
  emptyPageAction: 'Zurück zum Anfang',
  savedTitle: 'Gemerkt',
  savedEmpty: 'Du hast noch nichts gemerkt. Auf jedem Termin gibt es einen Merken-Knopf — danach steht er hier.',
  savedAnonymous: 'Diese Liste hängt an diesem Browser, nicht an einem Konto — sie verschwindet, wenn du die Seitendaten löschst oder das Gerät wechselst.',
  savedAnonymousAction: 'E-Mail hinterlegen',
  savedAnonymousAfter: ', dann bleibt sie.',
  savedCancelled: (count) => (count === 1
    ? 'Ein gemerkter Termin wurde abgesagt.'
    : `${count} gemerkte Termine wurden abgesagt.`),
  savedRetention: 'Weiter zurück geht es nicht: gemerkte Termine werden nach zwölf Monaten gelöscht. Was bleibt, ist die Zahl am Termin selbst.',

  // publisher application
  applyTitle: 'Seite übernehmen',
  applyLede: 'Anlegen kannst du eine Institution selbst. Von Hand geprüft wird nur, ob du wirklich dazugehörst — danach zieht die Seite auf die kurze Adresse um.',
  applyVenueName: 'Name der Institution',
  applyCity: 'Stadt',
  applyAddress: 'Adresse',
  applyWebsite: 'Website oder Instagram',
  applyContact: 'Wie erreichen wir dich?',
  applyNote: 'Was macht ihr?',
  applySubmit: 'Antrag schicken',
  applyNameMissing: 'Wie heißt die Institution?',
  applyPendingTitle: 'Antrag läuft',
  applyPending: (venue) => `Dein Antrag für ${venue} liegt bei uns. Wir melden uns, sobald jemand draufgeschaut hat.`,

  // Übernahme einer Seite, die es schon gibt. Bewusst dasselbe Formular wie der
  // Antrag: es ist derselbe Vorgang für die Moderation, und ein zweites
  // Formular wäre ein zweiter Weg, denselben Fehler zu machen.
  claimTitle: 'Seite übernehmen',
  claimLede: (venue) => `Du beantragst, ${venue} zu übernehmen. Wir prüfen von Hand, dass du wirklich dazugehörst — danach gehört die Seite dir und du kannst sie gestalten.`,
  claimKeepsProgramme: 'Das eingetragene Programm bleibt erhalten. Wer es bisher gepflegt hat, bleibt als Redaktion dabei, bis du das änderst.',
  backToCalendar: 'Zurück zum Kalender',

  // account
  accountTitle: 'Mein Konto',
  tabProfile: 'Profil',
  tabPage: 'Seite',
  tabAccount: 'Konto',
  tabData: 'Daten',
  fieldAvatar: 'Profilbild',
  fieldRole: 'Rolle',
  fieldVenues: 'Institutionen',
  roleGuest: 'Gast',
  rolePerson: 'Person',
  // Kein Rang, sondern die Kontoart. Der Schlüssel wird an drei Stellen
  // gelesen — `/settings`, der Slug-Router und die Nutzerliste — und fehlte in
  // beiden Katalogen, seit `institution` aufhörte, ein Rang zu sein: alle drei
  // rendern seither einen leeren String, wo die Art stehen sollte.
  roleInstitution: 'Institution',
  roleModerator: 'Moderation',
  roleAdmin: 'Administration',
  signOut: 'Abmelden',
  signOutHint: 'Meldet dich auf diesem Gerät ab. Die Sitzung wird ungültig, nicht nur der Cookie gelöscht.',
  signOutEverywhere: 'Überall abmelden',
  signOutEverywhereHint: 'Beendet alle Sitzungen auf allen Geräten, auch die hier. Nimm das, wenn du ein Gerät verloren hast.',
  yourDataTitle: 'Deine Daten',
  yourData: 'Wir speichern deine E-Mail, optional einen Namen, deine Zusagen und wem du folgst. Keinen echten Namen, keine Adresse, keine Standortdaten, keine Werbe-IDs.',
  downloadAll: 'Alles herunterladen',
  digestTitle: 'Wochenmail',
  digestLede: 'Einmal pro Woche, was die Institutionen angekündigt haben, denen du folgst. Nur wenn du willst, und in jeder Mail steht ein Abmelde-Link.',
  digestSubscribe: 'Wochenmail bekommen',
  digestUnsubscribe: 'Wochenmail abbestellen',
  deleteTitle: 'Konto löschen',
  deleteLede: 'Löschen entfernt E-Mail, Zusagen und Follows sofort und dauerhaft. In den Sicherungen verschwinden sie innerhalb von sieben Tagen.',
  deleteMemberWarning: (count) => (count === 1
    ? 'Achtung: du pflegst eine Institution. Ihr Programm bleibt bestehen, deine Mitgliedschaft nicht.'
    : `Achtung: du pflegst ${count} Institutionen. Ihr Programm bleibt bestehen, deine Mitgliedschaft nicht.`),
  deleteConfirmWord: 'LÖSCHEN',
  deleteConfirmLabel: 'Zum Bestätigen LÖSCHEN eintippen',
  deleteSubmit: 'Konto endgültig löschen',

  passkeyTitle: 'Sicherheitsschlüssel',
  passkeyLede: 'Für Moderation und Administration ist ein Schlüssel Pflicht. Er kommt zum Code dazu und ersetzt ihn nicht — ein verlorenes Telefon soll kein verlorenes Konto sein.',
  passkeyAdd: 'Schlüssel hinzufügen',
  passkeyLabel: 'Bezeichnung (damit du ihn wiedererkennst)',
  passkeyNone: 'Noch kein Schlüssel hinterlegt.',
  passkeyAdded: (when) => `hinzugefügt am ${when}`,
  passkeyLastUsed: (when) => `zuletzt benutzt am ${when}`,
  passkeyNeverUsed: 'noch nicht benutzt',
  passkeyRemove: 'Entfernen',
  passkeyRemoveConfirm: 'Diesen Schlüssel wirklich entfernen?',
  passkeyUnsupported: 'Dieser Browser kann keine Sicherheitsschlüssel. Nimm einen anderen oder ein anderes Gerät.',
  passkeyFailed: 'Das hat nicht geklappt. Versuch es noch einmal.',
  passkeyStaleCode: 'Einen Schlüssel hinterlegen darfst du nur direkt nach der Anmeldung. Melde dich ab und mit einem neuen Code wieder an, dann geht es.',
  passkeyEnrolling: 'Bevor du weitermachen kannst, brauchst du einen Sicherheitsschlüssel. Moderation und Administration sind gesperrt, bis einer hinterlegt ist.',
  passkeyStepTitle: 'Schlüssel bestätigen',
  passkeyStepLede: 'Der Code stimmt. Jetzt noch dein Sicherheitsschlüssel — erst dann entsteht eine Sitzung.',
  passkeyStepButton: 'Mit Schlüssel anmelden',
  passkeyStepExpired: 'Das hat zu lange gedauert. Melde dich noch einmal an.',

  // login
  signInTitle: 'Anmelden',
  signInLede: 'Wir schicken dir einen sechsstelligen Code.',
  fieldEmail: 'E-Mail',
  requestCode: 'Code anfordern',
  codeTitle: 'Code eingeben',
  codeLede: (minutes) => `Der Code gilt ${minutes} Minuten und nur in diesem Fenster.`,
  fieldCode: 'Code',
  // Names the consequence, not a feeling: this is the one control that decides
  // how long a session to the most sensitive data in the product stays open,
  // and "vertrauen" describes neither the year nor the device it applies to.
  trustDevice: 'Auf diesem Gerät ein Jahr angemeldet bleiben (sonst 90 Tage)',
  resendCode: 'Neuen Code schicken',
  honeypotLabel: 'Website',

  // Die Ein-Klick-Liste, die es nur lokal gibt. Der Satz nennt beide Orte, an
  // denen der Code landet — die Konsole allein ist nicht da, wo jemand mit
  // Server hinter einer IDE hinsieht.
  devLoginTitle: 'Nur lokal',
  devLoginNote: (logFile) => `Der Code steht sonst auf der Server-Konsole und in ${logFile}. Diese Liste erscheint nur, solange MAIL_DRIVER=log gesetzt ist und der Build kein Produktionsbuild ist.`,

  errInvalidEmail: 'Diese Adresse sieht nicht richtig aus.',
  errWrongCode: 'Der Code stimmt nicht.',
  errExpired: 'Der Code ist abgelaufen. Fordere einen neuen an.',
  errLocked: 'Zu viele Versuche. Fordere einen neuen Code an.',
  errWrongBrowser: 'Dieser Code gehört zu einem anderen Fenster. Fordere hier einen neuen an.',
  errRateLimited: 'Zu viele Anfragen. Versuch es in ein paar Minuten noch mal.',
  errBanned: 'Dieses Konto ist gesperrt.',
  errGeneric: 'Das hat nicht geklappt.',
  // ------------------------------------------------------------- backend
  //
  // Everything the Vite app renders. It lives here rather than in app/i18n.js
  // so there is one catalogue, not two that drift — app/i18n.js is a consumer
  // now, and the parity test covers these keys like every other.

  backend: 'Verwaltung',
  venuesEmptyTitle: 'Du pflegst noch keine Institution.',
  venuesEmptyHint: 'Leg eine an — Name und Stadt genügen. Alles andere kannst du später ergänzen.',
  newInstitution: 'Neue Institution',
  newInstitutionHint: 'Deine Seite liegt zunächst unter tba.tips/i/name. Sobald wir bestätigt haben, dass du dazugehörst, zieht sie auf die kurze Adresse um.',
  institutionNameRequired: 'Ohne Namen geht es nicht.',
  createFailed: 'Anlegen hat nicht geklappt.',
  series: 'Veranstaltungen',
  seriesEmpty: 'Noch nichts eingetragen.',
  owner: 'Inhaber',
  editor: 'Redaktion',
  published: 'öffentlich',
  draft: 'Entwurf',
  loading: 'Lädt …',
  notFound: 'Diese Seite gibt es in der Verwaltung nicht.',
  failed: 'Das hat nicht geklappt.',
  signedOut: 'Nicht angemeldet.',
  toWebsite: 'Zur Website',
  switchTheme: 'Zwischen hell und dunkel wechseln',

  calendar: 'Kalender',
  toToday: 'Heute',
  noEventsThisMonth: 'In diesem Monat ist nichts eingetragen.',
  cancelledShort: 'abgesagt',
  movedShort: 'verlegt',

  edit: 'Bearbeiten',
  move: 'Verschieben',
  cancelEvent: 'Absagen',
  reactivate: 'Wieder ansetzen',
  pickTarget: 'Zieltag wählen — Tab bewegt, Enter setzt, Esc bricht ab',
  // Every day cell becomes a button while a move runs, so each needs a name a
  // screen reader can read out on its own. "15" is not one.
  moveTo: (date) => `Verschieben auf ${longDate(date)}`,
  moveFailed: 'Verschieben hat nicht geklappt.',
  moveConflict: 'Der Termin wurde inzwischen von jemand anderem geändert.',
  moveOccupied: 'An diesem Tag läuft die Reihe schon.',
  cancelFailed: 'Das hat nicht geklappt.',
  confirmPast: 'Dieser Tag liegt in der Vergangenheit. Trotzdem verschieben?',

  newOccurrence: 'Neuer Termin',
  fieldTitle: 'Titel',
  fieldStartDate: 'Datum',
  fieldStartTime: 'Beginn',
  fieldEndDate: 'Letzter Tag',
  fieldMultiDay: 'Geht über mehrere Tage',
  fieldNone: '—',
  save: 'Speichern',
  cancelAction: 'Abbrechen',
  titlePrompt: 'Fang mit einem Titel an.',
  endBeforeStart: 'Der letzte Tag liegt vor dem Beginn.',
  saveFailed: 'Speichern hat nicht geklappt.',
  createdDraft: 'Angelegt — als Entwurf. Öffentlich wird er erst, wenn du ihn freigibst.',

  recurrence: 'Wiederholung',
  repeats: 'Findet statt',
  freqOnce: 'einmalig',
  freqWeekly: 'wöchentlich',
  freqMonthly: 'monatlich',
  everyWeek: 'jede Woche',
  every2Weeks: 'jede 2. Woche',
  every3Weeks: 'jede 3. Woche',
  every4Weeks: 'jede 4. Woche',
  onWeekday: 'am',
  byWeekday: 'am Wochentag',
  byDate: 'am Datum',
  first: 'ersten',
  second: 'zweiten',
  third: 'dritten',
  fourth: 'vierten',
  last: 'letzten',
  untilLabel: 'bis',
  nextDates: 'Nächste Termine',
  noDates: 'Mit dieser Regel entsteht kein Termin.',

  fieldSubtitle: 'Untertitel',
  fieldDescription: 'Beschreibung',
  fieldDuration: 'Dauer in Minuten',
  fieldPrice: 'Eintritt',

  // The facet editor. The style label changes with the domain — Genre under
  // Music, Medium under Art, Küche under Food — because "genre" is the wrong
  // word outside music and a single label would be wrong twice out of three.
  fieldFacets: 'Einordnung',
  fieldDomain: 'Bereich',
  fieldFormat: 'Format',
  fieldStyle: 'Stil',
  fieldSetting: 'Drinnen oder draußen',
  facetStyleMusic: 'Genre',
  facetStyleArt: 'Medium',
  facetStyleFood: 'Küche',
  facetHint: 'Wähle zuerst einen Bereich — Formate und Stile erscheinen dann. Alles optional, solange mindestens ein Tag oder eine Facette gesetzt ist.',
  classificationRequired: 'Setze mindestens ein Tag oder eine Facette, sonst ist der Termin nur über sein Datum auffindbar.',

  fieldTicketUrl: 'Ticket-Link',
  fieldLinks: 'Weitere Links',
  fieldLinksDate: 'Links nur für diesen Termin',
  linkLabel: 'Beschriftung',
  linkUrl: 'Link-Adresse',
  addLink: 'Link hinzufügen',
  removeLink: 'Link entfernen',
  linkHint: 'Nur http(s). Ohne Beschriftung zeigen wir die Domain. Höchstens sechs pro Termin.',
  linkBadUrl: 'Das ist keine gültige http(s)-Adresse.',
  linkTooMany: 'Höchstens sechs Links pro Termin — Serie und Termin zusammen.',
  editEvent: 'Termin bearbeiten',
  // The base element is an event. A series is what an event can turn into
  // once it repeats, so the thing you create is never called a series.
  newEvent: 'Neuer Termin',
  publish: 'Veröffentlichen',
  unpublish: 'Zurückziehen',
  saved: 'Gespeichert.',
  publishedNow: 'Ist jetzt öffentlich.',
  unpublishedNow: 'Ist jetzt ein Entwurf und nicht mehr öffentlich.',

  editVenue: 'Institution bearbeiten',
  fieldVenueName: 'Name',
  fieldCity: 'Stadt',
  fieldAddress: 'Adresse',
  fieldWebsite: 'Website',
  fieldProfileImage: 'Profilbild',
  fieldSlug: 'Kurz-URL',
  slugWarning: 'Die Kurz-URL steht in eurer Instagram-Bio. Alte Adressen leiten dauerhaft weiter, aber ändere sie nur, wenn es sein muss.',
  ownerOnly: 'Profil, Kurz-URL, Kalenderseite und Team ändert nur der Inhaber. Programm pflegen und exportieren kannst du.',
  confirmRename: 'Kurz-URL wirklich ändern? Die alte leitet weiter, aber gedruckte Links und Aushänge stimmen dann nicht mehr.',
  slugRejected: 'Diese Kurz-URL geht nicht — sie ist vergeben, reserviert oder zu kurz.',
  venueNameRequired: 'Die Institution braucht einen Namen.',

  bulkActions: 'Aktionen',
  pauseSeries: 'Pausieren',
  pauseFrom: 'Pause von',
  pauseTo: 'bis',
  pauseHint: 'Die Reihe läuft danach normal weiter — die Regel bleibt, nur diese Tage fallen aus.',
  duplicateSeries: 'Duplizieren',
  copySuffix: ' (Kopie)',
  shiftSeries: 'Verschieben um',
  shiftDays: 'Tage',
  shiftHint: 'Verschiebt die ganze Reihe. Aus „jeden Freitag“ plus ein Tag wird „jeden Samstag“.',
  paused: 'Pause eingetragen.',
  duplicated: 'Kopie angelegt — als Entwurf.',
  shifted: 'Reihe verschoben.',
  actionFailed: 'Das hat nicht geklappt.',
  shiftMonthOverflow: 'So weit geht das nicht — die Reihe würde in einen anderen Monat rutschen.',
  shiftNotShiftable: 'Eine Reihe am „ersten Freitag im Monat“ lässt sich nicht um Tage verschieben. Ändere stattdessen die Regel.',

  artwork: 'Flyer',
  addImage: 'Bild hinzufügen',
  dropImages: 'Bilder hierher ziehen oder klicken',
  imageRotates: 'rotiert automatisch',
  imageUnnamed: 'ohne Beschreibung',
  imageAlt: 'Bildbeschreibung',
  removeImage: 'Entfernen',
  remove: 'entfernen',
  fieldTags: 'Tags',
  tagPlaceholder: 'Tippen und aus der Liste wählen',
  tagHint: 'Bis zu fünf. Nimm bestehende Tags, wo es passt — neue nur, wenn wirklich keins passt.',
  tagLimitReached: 'Fünf Tags sind das Maximum.',
  tagExisting: 'vorhanden',
  tagCreate: 'neu anlegen',
  altLabel: 'Bildbeschreibung',
  altPlaceholder: 'Was steht auf dem Flyer?',
  uploading: 'Wird hochgeladen …',
  uploadFailed: 'Das Bild konnte nicht verarbeitet werden.',
  rotationHint: 'Mehrere Bilder wechseln sich über die Termine ab. Ein an ein Datum geheftetes Bild gewinnt.',
  imagesStripped: 'Beim Hochladen werden Bilder neu kodiert — Standortdaten aus dem Foto verschwinden dabei.',

  editOccurrence: 'Nur dieser Termin',
  fieldTitleOverride: 'Abweichender Titel',
  fieldDescriptionOverride: 'Abweichende Beschreibung',
  fieldPinImage: 'Bild für diesen Termin',
  overrideHint: 'Leer lassen heißt: nimm, was in der Reihe steht.',

  switchAccount: 'Konto wechseln',

  usersLede: 'Ein Konto gezielt suchen und öffnen: sperren, bestätigen, Auskunft geben, löschen. Ränge werden hier nicht vergeben.',
  usersSearch: 'E-Mail oder Name',
  usersEmpty: 'Niemand gefunden.',
  usersBanned: 'Gesperrt',
  usersBan: 'Sperren',
  usersUnban: 'Entsperren',
  usersBanReason: 'Grund (steht in der Sperrmeldung)',
  usersSince: 'Dabei seit',

  designNeedsClaim: 'Gestalten geht erst, wenn jemand aus der Institution das Konto übernommen hat. Bis dahin behält die Seite das neutrale Aussehen — wir veröffentlichen kein Erscheinungsbild, das die Institution nie gesehen hat.',
  designClaimLink: 'Konto übernehmen',

  membershipSearch: 'Institution suchen',
  membershipRole: 'Rolle',
  membershipReason: 'Begründung (steht im Protokoll)',
  membershipNonePicked: 'Noch keine Institution ausgewählt.',
  membershipPicked: (name) => `Ausgewählt: ${name}`,
  membershipHint: 'Der normale Weg ist die Einladung: die Person bekommt eine Mail und nimmt an. Eine Mitgliedschaft ist das Recht, unter fremdem Namen zu veröffentlichen — die Zustimmung gehört in den Vorgang und nicht dahinter.',
  membershipInvite: 'Einladen',
  membershipInvited: 'Einladung verschickt.',
  membershipAssign: 'Ohne Einladung zuweisen',
  membershipAssignHint: 'Nur Administration, und nur wenn eine Einladung nicht geht — Inhaberin weg, Adresse tot, Onboarding am Telefon. Wird als eigener Vorgang protokolliert, damit die Ausnahme im Protokoll als Ausnahme lesbar bleibt.',
  membershipAssignConfirm: (name) => `Ohne Zustimmung zu „${name}“ hinzufügen? Diese Person kann danach unter diesem Namen veröffentlichen, ohne gefragt worden zu sein.`,
  membershipAssigned: 'Zugewiesen.',
  membershipNoAddress: 'Dieses Konto hat keine Adresse — einladen geht erst nach der ersten Anmeldung.',
  membershipRemove: 'Entfernen',
  membershipRemoveReason: 'Warum wird die Mitgliedschaft entzogen? (steht im Protokoll)',
  membershipRemoved: 'Mitgliedschaft entzogen.',

  usersRank: 'Rang',
  usersRankHint: 'Nur die Administration vergibt Moderation. Wer moderiert, vergibt selbst keine Ränge — sonst reicht ein unvorsichtiges Konto, um die Tür für alle zu öffnen. Administration wird hier nicht vergeben: dafür braucht es einen Zugang zum Server, und von dort kommt auch die allererste.',
  usersRankGrant: 'Zur Moderation machen',
  usersRankRevoke: 'Moderation entziehen',
  usersRankReason: 'Begründung (steht im Protokoll)',
  usersRankGrantConfirm: (name) => `„${name}“ zur Moderation machen? Damit sieht diese Person Meldungen, Sperren und — bei gezieltem Öffnen eines Kontos — auch Zusagen und gefolgte Konten.`,
  usersRankRevokeConfirm: (name) => `„${name}“ die Moderation entziehen? Offene Sitzungen werden sofort ungültig.`,
  usersRankAdminCli: 'Änderbar nur über den Server (npm run grant).',

  adminPasskeys: 'Sicherheitsschlüssel',
  adminPasskeysNone: 'Kein Schlüssel hinterlegt — dieses Konto steckt im Einschreibefenster.',
  adminPasskeyRevoke: 'Entziehen',
  adminPasskeyReason: 'Warum? (steht im Protokoll)',
  adminPasskeyRevoked: 'Schlüssel entzogen. Offene Sitzungen sind ungültig.',
  adminPasskeyHint: 'Entziehen macht laufende Sitzungen ungültig. Ist es der letzte Schlüssel, öffnet sich das Einschreibefenster wieder — die Person meldet sich mit Code an und registriert einen neuen.',

  usersSearchFirst: 'Tipp einen Namen oder eine E-Mail ein. Es gibt hier bewusst keine Liste zum Durchblättern — ein Konto wird gezielt geöffnet.',
  usersVerified: 'Bestätigt',
  usersUnverified: 'Nicht bestätigt',
  usersVerification: 'Bestätigung',
  usersVerify: 'Bestätigen',
  usersVerifyReason: 'Woher weißt du das? (steht im Protokoll)',
  usersVerifyHint: 'Bestätigt heißt: jemand aus dieser Institution hat das Konto übernommen. Das schaltet die kurze Adresse im Wurzelverzeichnis frei und erlaubt der Institution, ihre Kalenderseite zu gestalten. Ränge werden hier nicht vergeben — wer aus einem Dashboard heraus jemanden zur Moderation machen kann, hat einen Weg nach oben gebaut.',
  usersVerifyIrreversible: 'Zurücknehmen geht hier nicht: die kurze Adresse steht dann in Instagram-Profilen, und sie wieder wegzunehmen macht daraus einen toten Link. Für ein Konto, das Ärger macht, ist die Sperre das Mittel.',

  usersExport: 'Alle Daten herunterladen',
  usersExportPurpose: 'Wofür? (steht im Protokoll)',
  usersExportHint: 'Auskunft nach Art. 15 — dieselbe Datei, die die Person selbst unter „Einstellungen“ bekommt. Gedacht für die, die sich nicht anmelden kann und deshalb geschrieben hat. Immer nur ein Konto, immer protokolliert.',

  usersOpen: 'Konto öffnen',
  usersBack: 'Zurück zur Suche',
  usersDetail: 'Konto',
  usersLastSeen: 'Zuletzt gesehen',
  usersNever: 'nie',
  usersMemberships: 'Gehört zu',
  usersNoMemberships: 'Keine Mitgliedschaft.',
  usersReportsFiled: (n) => `${n} ${n === 1 ? 'Meldung' : 'Meldungen'} abgeschickt`,
  usersReportsReceived: (n) => `${n} ${n === 1 ? 'Meldung' : 'Meldungen'} erhalten`,

  // Die Zweckangabe steht in der Oberfläche, weil sie zum Feature gehört und
  // nicht daneben: Wer hier klickt, soll lesen, wofür das gedacht ist, bevor
  // etwas sichtbar wird — und dass es protokolliert wird.
  activityHeading: 'Zusagen und gefolgte Konten',
  activityPurpose: 'Nur zur Aufklärung einer Meldung. Was hier steht, ist ein Interessenprofil und wird nirgendwo sonst angezeigt — auch dem Veranstalter nicht.',
  activityLogged: 'Das Öffnen wird protokolliert, mit deinem Namen und dem Grund, den du angibst.',
  activityReason: 'Wofür? (steht im Protokoll)',
  activityOpen: 'Aktivität ansehen',
  activityEmpty: 'Nichts eingetragen.',
  activityVotes: 'Zusagen',
  activityFollows: 'Folgt',
  activityFailed: 'Konnte nicht geladen werden.',

  usersDelete: 'Konto löschen',
  usersDeleteHint: 'Löschen ist für eine begründete Anfrage nach Art. 17, nicht für Ärger. Das Alltagswerkzeug ist die Sperre: umkehrbar, nimmt aus der Veröffentlichung statt zu zerstören.',
  usersDeleteReason: 'Grund (steht im Protokoll, nicht in den Daten)',
  usersDeleteConfirm: (name) => `„${name}“ endgültig löschen? Zusagen, gefolgte Konten und Sitzungen gehen mit. Vergangene Termine bleiben, das Protokoll behält nur eine Kennung.`,
  usersDeleted: 'Gelöscht.',

  // The crawl dashboard. Three views of one pipeline: what came in, which
  // sources run, and what waits for a person.
  crawlTitle: 'Crawl',
  crawlLede: 'Was der Crawler geholt hat, welche Quellen laufen, und was auf eine Freigabe wartet.',
  crawlTabOverview: 'Überblick',
  crawlTabSources: 'Quellen',
  crawlTabQueue: 'Warteschlange',
  crawlStart: 'Crawl starten',
  crawlStartHint: 'Reiht einen Lauf ein. Er beginnt in wenigen Sekunden und dauert einige Minuten.',
  crawlRunning: (since) => `Läuft seit ${since}`,
  crawlQueued: 'Eingereiht — startet gleich',
  crawlOff: 'Der Crawler ist abgeschaltet.',
  crawlOffHint: 'CRAWL steht auf off. Das ist der Notaus, und er gilt auch für diesen Knopf.',
  crawlPending: (n) => `${n} wartend`,
  crawlPromotable: (n) => `${n} freigebbar`,
  crawlNoTimeCount: (n) => `${n} ohne Startzeit`,
  crawlNoVenueCount: (n) => `${n} ohne Betrieb`,
  crawlProblems: 'Probleme',
  crawlNoProblems: 'Keine Quelle braucht gerade Aufmerksamkeit.',
  crawlHealthOk: 'Liefert',
  crawlHealthStalled: 'Lieferte früher, jetzt nichts',
  crawlHealthNever: 'Hat noch nie etwas geliefert',
  crawlHealthDisabled: 'Abgeschaltet',
  crawlUnchecked: 'robots/TDM nicht erfasst',
  crawlUncheckedCount: (n, total) => `${n} von ${total} Quellen ohne erfassten robots/TDM-Zustand`,
  crawlUncheckedHint: 'Ohne erfassten Zustand gilt eine Quelle als ungeprüft, nicht als erlaubt.',
  crawlYield: (last, total) => `zuletzt ${last} · insgesamt ${total}`,
  crawlRuns: 'Letzte Läufe',
  crawlStaged: (n) => `${n} neu`,
  crawlDisable: 'Abschalten',
  crawlEnable: 'Wieder einschalten',
  crawlDisableReason: 'Grund fürs Abschalten',
  crawlSources: (n) => `${n} Quellen`,
  crawlNoTime: 'Keine Startzeit — die Quelle nennt keine, und eine Zeit wird nie erfunden.',
  crawlNeedsVenue: 'Diese Quelle hat noch keinen Betrieb. Erst zuordnen, dann freigeben:',
  crawlNeedsClassification: 'Diese Quelle hat kein Facetten-Vorgabe — Facetten oder Tags hier setzen.',
  crawlVenueCommand: (key) => `npm run crawl:review -- venue ${key} <slug> <Name>`,
  crawlApprove: (n) => `${n} freigeben`,
  crawlReject: 'Ablehnen',
  crawlRejectReason: 'Grund für die Ablehnung',
  crawlSelectAll: 'Alle auswählen',
  crawlSelectBlocked: 'Blockierte einbeziehen',
  crawlNothingSelected: 'Nichts ausgewählt.',
  crawlOpenSource: 'Quelle öffnen',
  crawlYearGuessed: 'Jahr geraten',
  crawlNoStartTime: 'KEINE ZEIT',
  crawlRoomHint: (room) => `Ort? ${room}`,
  crawlRoom: (room) => `Ort: ${room}`,
  crawlPriceFree: 'Eintritt frei',
  crawlPrice: (price) => `Preis: ${price}`,
  crawlTicket: (host) => `Ticket: ${host}`,
  crawlFacets: 'Facetten (kind:slug, mit Komma getrennt)',
  crawlTags: 'Tags (mit Komma getrennt)',
  crawlApproved: (n) => `${n} freigegeben`,
  crawlRefusedCount: (n) => `${n} abgelehnt`,
  crawlEmptyQueue: 'Nichts wartet auf eine Freigabe.',

  stats: 'Statistik',
  statsLede: 'Was ankommt, was daraus wird, und was die Datenbank schon vorher wusste.',
  statsOff: 'Keine Statistik eingerichtet.',
  statsOffHint: 'ANALYTICS steht auf off. Lokal ist das der vorgesehene Zustand — die Zahlen kommen mit dem Server.',
  health: 'Betrieb',
  healthMail: 'E-Mail-Versand',
  healthMailHint: 'Login-Codes sind der einzige Anmeldeweg. Fällt der Versand aus, kommt niemand mehr rein.',
  healthWindow: 'Terminfenster',
  healthWindowHint: 'Wie weit Termine im Voraus tatsächlich existieren. Stockt der Job, dünnt der Feed von vorne aus, ohne dass etwas rot wird.',
  healthJobs: 'Letzte Läufe',
  healthNever: 'noch nie gelaufen',
  healthOk: 'läuft',
  healthStale: 'hängt',
  healthDays: (n) => `${n} ${n === 1 ? 'Tag' : 'Tage'}`,

  statsRangeLabel: 'Zeitraum',
  statsRange24h: 'Letzte 24 Stunden',
  statsRange7d: 'Letzte 7 Tage',
  statsRange30d: 'Letzte 30 Tage',
  statsRange90d: 'Letzte 90 Tage',
  statsRange365d: 'Letzte 365 Tage',
  statsPageviews: 'Seitenaufrufe',
  statsVisitors: 'Besuche',
  statsUnreachable: 'Die Statistik antwortet gerade nicht. Die Seite läuft trotzdem — sie hängt nie daran.',
  statsNoToken: 'Kein Lesezugriff eingerichtet. Die Zahlen holt der Server, nie der Browser — dafür fehlt hier noch das Token.',
  statsFailed: 'Die Anfrage ist fehlgeschlagen — keine abgeschaltete Analytics, sondern eine abgelaufene Sitzung oder ein Serverfehler.',
  statsAggregate: 'Nur Summen. Etwas anderes haben wir nicht: IPs werden gehasht und verworfen.',

  statsTileVisitors: 'Besucher',
  statsTileCalendarAdds: 'Kalendereinträge',
  statsTileTicketClicks: 'Ticket-Klicks',
  statsTraffic: 'Zugriffe über Zeit',
  statsFunnel: 'Trichter',
  statsVenues: 'Kalenderseiten-Aufrufe je Institution',
  statsNoData: 'Noch keine Daten.',

  statsInventory: 'Bestand',
  statsInventoryFailed: 'Der Bestand konnte nicht geladen werden — keine leere Datenbank, sondern ein Serverfehler.',
  statsPublishing: 'Mit Programm',
  statsIdle: 'Ohne Programm',
  statsPublished7d: 'Neu veröffentlicht (7 Tage)',
  statsNext7: 'Nächste 7 Tage',
  statsNext30: 'Nächste 30 Tage',
  statsByCity: 'Nach Stadt (30 Tage)',
  statsIncomplete: 'Unvollständige Reihen',
  statsNoImage: 'Ohne Bild',
  statsNoTag: 'Ohne Tag',
  statsNoAddress: 'Ohne Adresse',

  statsCanary: 'Login-Codes',
  statsCanaryHint: 'Eine Lücke zwischen den beiden Zahlen heißt: Mail kommt nicht an. Der Login-Code ist der einzige Anmeldeweg.',
  statsCanaryCount: (consumed, issued) => `${consumed} von ${issued} Codes eingelöst`,

  healthDatabase: 'Datenbank',
  healthDatabaseHint: 'Wächst das Write-Ahead-Log immer weiter, wird nicht mehr eingecheckt — das fällt als Plattendruck auf, lange bevor etwas einen Fehler wirft.',
  healthWal: 'davon WAL',
  healthSchema: 'Schema',
  healthSchemaApplied: (n) => `${n} ${n === 1 ? 'Migration' : 'Migrationen'} angewandt`,
  healthBackup: 'Letzte Sicherung',
  healthDrill: 'Letzte Rückspielprobe',
  healthDrillHint: 'Eine Sicherung, die nie zurückgespielt wurde, ist eine Vermutung.',
  healthRateLimit: 'Limit-Treffer',
  healthRateLimitHint: 'Das billigste Missbrauchssignal, das es gibt: ein volles Fenster heißt, jemand ist gegen eine Wand gelaufen.',
  healthBytes: (mb) => `${mb} MB`,

  navCollaborations: 'Beteiligt',
  collaborationsLede: 'Veranstaltungen, bei denen du als Beteiligte eingetragen wurdest. Auf deiner Seite erscheinen sie erst, wenn du zusagst.',
  collaborationsEmpty: 'Dich hat noch niemand eingetragen.',
  collaborationAccept: 'Zusagen',
  collaborationDecline: 'Absagen',
  collaborationLeave: 'Von meiner Seite nehmen',
  collaborationAccepted: 'Zugesagt',
  collaborationDeclined: 'Abgesagt',
  collaborationPending: 'Offen',
  collaborationBy: (host) => `von ${host}`,
  fieldCollaborators: 'Beteiligte',
  collaboratorsHint: 'Wen du hier einträgst, wird gefragt. Auf deren Seite steht die Veranstaltung erst nach der Zusage.',
  collaboratorAdd: 'Einladen',
  collaboratorRemove: 'Einladung zurückziehen',
  collaboratorSearch: 'Name der Person oder Institution',

  ownProgramme: 'Mein Programm',
  ownProgrammeLede: 'Was du selbst veranstaltest.',
  ownProgrammeEmpty: 'Du hast noch nichts eigenes eingetragen.',
  navInstitutions: 'Institutionen',
  backToPanel: 'Zurück zur Übersicht',
  fieldOwner: 'Wer veranstaltet',
  ownerHint: 'Entscheidet, auf wessen Seite die Veranstaltung steht. Später nicht mehr änderbar.',

  institutionsTitle: 'Institutionen',
  institutionsLede: 'Für Clubs, Bars, Galerien, Kollektive und alle, die regelmäßig ein Programm machen. Eine Institution hat eine eigene Kalenderseite, ein Team und einen Export — und eine kurze Adresse, sobald wir bestätigt haben, dass du dazugehörst.',

  deleteSeries: 'Veranstaltung löschen',
  deleteSeriesConfirm: (title, upcoming) => (upcoming === 1
    ? `„${title}“ löschen? Ein kommender Termin verschwindet damit von eurer Seite.`
    : `„${title}“ löschen? ${upcoming} kommende Termine verschwinden damit von eurer Seite.`),
  deleteSeriesHint: 'Verschwindet von eurer Seite und aus dem Feed. Vergangene Termine bleiben im Export erhalten — gelöscht wird nichts weggeworfen.',
  deletedSeries: 'Gelöscht.',

  moderation: 'Moderation',
  moderationLede: 'Anträge, Meldungen und was die Jobs zuletzt gemacht haben.',
  moderatorOnly: 'Diese Seite ist der Moderation vorbehalten.',
  moderatorNeedsPasskey: 'Die Moderation bleibt gesperrt, bis ein Sicherheitsschlüssel hinterlegt ist. Das geht unter Einstellungen.',
  applications: 'Anträge',
  reports: 'Meldungen',
  nothingPending: 'Nichts offen.',
  reviewNote: 'Notiz (optional)',
  approve: 'Freischalten',
  reject: 'Ablehnen',
  approved: 'Freigeschaltet:',
  rejected: 'Abgelehnt.',
  confirmReject: 'Antrag wirklich ablehnen?',
  markReviewed: 'Erledigt',
  reportResolved: 'Meldung erledigt.',
  anonymous: 'anonym',

  reportAge: (h) => (h < 48 ? `vor ${h} h` : `vor ${Math.floor(h / 24)} Tagen`),
  reportDue: (date) => `Frist: ${date}`,
  reportOverdue: 'Frist überschritten',
  reportDeadlineErasure: 'Löschanfrage',
  reportContactBack: (contact) => `Antwort an: ${contact}`,
  reportNoContact: 'Keine Rückadresse angegeben.',
  reportReasonGiven: 'Begründung (Pflicht, geht ins Protokoll)',
  reportUnpublish: 'Aus der Veröffentlichung nehmen',
  reportBan: 'Konto sperren',
  reportDismiss: 'Nichts zu tun',
  reportNeedsReason: 'Ohne Begründung nicht — auch „nichts zu tun“ braucht eine.',
  reportNoAccount: 'Dahinter steht kein Konto, das gesperrt werden könnte.',
  reportUnpublished: (n) => (n === 1
    ? 'Erledigt. Eine Veranstaltung ist aus der Veröffentlichung genommen.'
    : `Erledigt. ${n} Veranstaltungen sind aus der Veröffentlichung genommen.`),
  reportTargetHint: 'Genommen wird die Reihe, nicht der einzelne Termin — „abgesagt“ wäre eine Aussage über die Veranstaltung und stünde in jedem abonnierten Kalender.',
  jobRuns: 'Letzte Job-Läufe',
  noJobRuns: 'Noch kein Lauf verzeichnet.',
  auditLog: 'Protokoll',

  team: 'Team',
  teamHint: 'Wer hier steht, kann das Programm pflegen. Einzeln einladen statt ein gemeinsames Passwort — das lässt sich zurücknehmen, wenn jemand geht.',
  roleOwner: 'Inhaber',
  roleEditor: 'Redaktion',
  sendInvite: 'Einladen',
  inviteFailed: 'Einladung konnte nicht verschickt werden.',
  invitePending: 'eingeladen',
  revokeInvite: 'Zurückziehen',
  removeMember: 'Entfernen',
  confirmRemoveMember: 'Diese Person wirklich entfernen?',
  lastOwner: 'Der letzte Inhaber kann nicht entfernt werden.',
  close: 'Schließen',
  discardChanges: 'Ungespeicherte Änderungen verwerfen?',
  exportData: 'Daten exportieren',
  exportHint: 'Alles, was ihr eingetragen habt, als eine Datei. Ohne Nachfrage, jederzeit.',

  pageBuilder: 'Eure Seite',
  theme: 'Helligkeit',
  themeDark: 'dunkel',
  themeLight: 'hell',
  accent: 'Akzentfarbe',
  defaultView: 'Startansicht',
  howFarAhead: 'Wie weit voraus',
  month: 'Monat',
  months: 'Monate',
  whichSeries: 'Welche Reihen',
  preview: 'Vorschau',
  copyLink: 'Link kopieren',
  copied: 'Kopiert',
  copyFailed: 'Bitte von Hand kopieren',

  // Der Assistent und der Editor mit Reitern. Die drei Schritte heißen genauso
  // wie die drei Reiter: was man beim Anlegen gelernt hat, ist der Ort, an dem
  // man beim Ändern nachsieht.
  stepWhat: 'Was es ist',
  stepWhen: 'Wann',
  stepFound: 'Wie er gefunden wird',
  stepSeries: 'Serie',
  stepOf: (n, total) => `Schritt ${n} von ${total}`,
  back: 'Zurück',
  next: 'Weiter',
  saveDraft: 'Entwurf speichern',
  draftSaved: 'Entwurf gespeichert.',
  useFullForm: 'Alles auf einer Seite',
  useWizard: 'Schritt für Schritt',
  repeatsToggle: 'Wiederholt sich',
  flyerQueued: 'Wird hochgeladen, sobald der Entwurf gespeichert ist.',
  collaboratorsLater: 'Kollaborateure einladen kommt später — bis dahin gehören Gäste in die Beschreibung.',

  // Der Knopf sagt, was fehlt, statt sich grau zu schalten. Die Wörter sind
  // Satzteile einer Aufzählung, deshalb klein und ohne Punkt.
  publishMissing: (fields) => `Zum Veröffentlichen fehlt noch: ${LIST.format(fields)}.`,
  // Derselbe Satz, aber als Baukasten: Jedes fehlende Stück ist ein Link zur
  // Station, die danach fragt, und die Satzzeichen gehören in die Vorlage.
  stillMissing: (parts) => ['Fehlt noch: ', ...parts, '.'],
  missingTitle: 'ein Titel',
  missingPlace: 'eine Adresse',
  missingDate: 'ein Datum',
  missingSetting: 'drinnen oder draußen',
  missingClassification: 'ein Tag oder eine Facette',

  fieldPlace: 'Adresse',
  placeElsewhere: 'Findet woanders statt',
  placePlaceholder: 'Adresse suchen',
  placeHint: 'Aus dem Adressbuch wählen. Eine neue Adresse legst du bewusst an — sonst stehen nach einem Jahr acht Schreibweisen derselben darin.',
  placeExisting: 'vorhanden',
  placeCreate: 'neu anlegen',
  placeNewHeading: 'Neue Adresse anlegen',
  fieldPlaceName: 'Name der Adresse',
  placeCreateAction: 'Adresse anlegen',
  placeNameRequired: 'Die Adresse braucht einen Namen.',
  placeCreateFailed: 'Die Adresse konnte nicht angelegt werden.',

  // ---------------------------------------------------------- /intern/datenfluss
  //
  // The one page in the product that explains the product. Not linked, not
  // indexed, and written for somebody who has never seen the code.
  //
  // The vocabulary is the product's own and is not negotiable here: an
  // *Institution* is an account nobody can sign in as. Calling it a house, a
  // venue or a place on this page of all pages would introduce the fourth word
  // for a thing whose second word already caused four bugs.
  flowTitle: 'Wie tba.tips zusammenhängt',
  flowLede: 'Fünfzehn Dinge und was sie miteinander machen. Inhalt kommt von links herein, Menschen von rechts, und in der Mitte steht die Institution, die beides zusammenhält.',
  flowHidden: 'Diese Seite ist nirgends verlinkt und für Suchmaschinen gesperrt.',
  flowBoardDesc: 'Ein Diagramm aus fünfzehn beschrifteten Kästen, verbunden durch Pfeile. Dieselben Angaben stehen darunter als Text.',
  flowLegendHuman: 'Ein Schritt, an dem ein Mensch entscheidet',
  flowLegendHint: 'Einen Kasten antippen hebt hervor, was an ihm hängt. Nochmal antippen hebt es auf.',
  flowLegendReset: 'Hervorhebung aufheben',
  flowDetailsTitle: 'Im Einzelnen',

  flowNodeSource: 'Website der Institution',
  flowNodeSourceNote: 'HTML, RSS oder ICS — was die Institution ohnehin veröffentlicht. Jede Quelle steht mit Adresse und Lesemuster in der Quellenliste.',
  flowNodeCrawler: 'Crawler',
  flowNodeCrawlerNote: 'Fragt robots.txt, achtet auf TDM-Widersprüche, holt nur bei geändertem ETag. Nimmt Fakten — Titel, Datum, Uhrzeit, Ort. Keine Beschreibungstexte von Webseiten, keine Bilder.',
  flowNodeStaging: 'Zwischenlager',
  flowNodeStagingNote: 'Nichts davon ist öffentlich. Ein Eintrag wartet hier, bis ein Mensch ihn ansieht — deshalb geht der Pfeil hinaus von der Moderation und nicht vom Crawler.',
  flowNodeSeries: 'Veranstaltung',
  flowNodeSeriesNote: 'Eine Zeile, die die Regel trägt — bei einem gecrawlten Eintrag lautet die Regel „einmal“. Das ist die Reihenfolge, die überrascht: nicht der Termin wird zur Reihe, sondern jede Veranstaltung ist von Anfang an die Regel, und ein einzelner Abend ist eine Regel mit genau einem Datum. Umgekehrt hätte ein Termin ohne Regel keine Herkunft, und aus ihm eine Wiederholung zu machen hieße, ihn in etwas anderes zu verwandeln. Die Institution ist Eigentümerin, tipsBOT ist Autor — zwei verschiedene Felder, und die Karte zeigt beide.',
  flowNodeDates: 'Einzeltermine',
  flowNodeDatesNote: 'Nächtlich aus der Regel erzeugt, 18 Monate voraus. Handarbeit — Absagen, Verschiebungen, Überschreibungen — überlebt jeden Neuaufbau.',
  flowNodeBot: 'tipsBOT',
  flowNodeBotNote: 'Selbst eine Institution, mit eigener Seite. Es besitzt keine Veranstaltung, es hat sie aufgeschrieben — und es bleibt nach einer Übernahme als Bearbeiter, weil das gepflegte Programm der Grund ist, warum die Institution uns gefunden hat.',
  flowNodeInstitution: 'Institution',
  flowNodeInstitutionNote: 'Eine Zeile ohne E-Mail: ein Konto, als das sich niemand anmelden kann. Genau das macht es zur Institution — nicht ein Rang, nicht ein Häkchen.',
  flowNodePlace: 'Adresse',
  flowNodePlaceNote: 'Ein eigener Eintrag im Adressbuch. Das Konto ist die Identität, die Adresse ist der Ort — sie fallen am ersten Tag zusammen und trennen sich bei der ersten Veranstaltung woanders.',
  flowNodeMembership: 'Mitgliedschaft',
  flowNodeMembershipNote: 'Das einzige, was den Arbeitsbereich öffnet. Bearbeiter pflegen das Programm; Eigentümer zusätzlich Name, Adresse, Aussehen und Mitglieder. Drei Personen führen eine Institution — die Alternative wäre ein geteilter Login, den niemand entziehen kann.',
  flowNodePage: 'Öffentliche Seite',
  flowNodePageNote: 'Unbestätigt liegt sie unter /i/name, bestätigt in der Wurzel unter /name, und die alte Adresse leitet dauerhaft um. Der kurze Name wird verdient, nicht vergeben.',
  flowNodeVisitor: 'Besucherin',
  flowNodeVisitorNote: 'Kein Konto, keine Zeile. Ein Seitenaufruf legt nichts an — sonst hinterließe jeder Crawler eine Spur.',
  flowNodeGuest: 'Gast',
  flowNodeGuestNote: 'Beim ersten Schreiben entsteht eine anonyme Zeile, nie beim Lesen.',
  flowNodePerson: 'Person',
  flowNodePersonNote: 'Dieselbe Zeile wird ergänzt, nicht ersetzt. Deshalb gibt es keinen Zusammenführungs-Pfad — und deshalb kann keine Merkung verloren gehen oder doppelt auftauchen.',
  flowNodeModeration: 'Moderation',
  flowNodeModerationNote: 'Gibt gecrawlte Einträge frei, bestätigt Institutionen, bearbeitet Meldungen. Jede dieser Handlungen schreibt eine Zeile ins Protokoll.',
  flowNodeAdmin: 'Administration',
  flowNodeAdminNote: 'Der einzige Rang, der Ränge vergibt — und auch nur zwischen Person und Moderation. Wer Moderation verteilen dürfte und selbst Moderation wäre, könnte sie sich selbst geben.',

  flowEdgeReads: 'liest',
  flowEdgeStages: 'stellt ein',
  flowEdgeReleases: 'gibt frei',
  flowEdgeBecomes: 'wird zu',
  flowEdgeGenerates: 'erzeugt',
  flowEdgeAuthors: 'ist Autor',
  flowEdgeOwns: 'ist Eigentümerin',
  flowEdgeRuns: 'betreibt',
  flowEdgeAppearsAs: 'erscheint als',
  flowEdgeOpens: 'öffnet',
  flowEdgeUntilClaimed: 'bis zur Übernahme',
  flowEdgeAccepts: 'nimmt Einladung an',
  flowEdgeApplies: 'stellt Antrag',
  flowEdgeConfirms: 'bestätigt',
  flowEdgeGrantsRank: 'vergibt den Rang',
  flowEdgeFirstWrite: 'erste Merkung',
  flowEdgeAddsEmail: 'E-Mail dazu',
  flowEdgeSaves: 'merkt sich',

  flowGatesTitle: 'Zwei Tore, die oft verwechselt werden',
  flowGateMembership: 'Mitgliedschaft entscheidet, was jemand anfassen darf — und sie hat nichts mit dem Rang zu tun. Eine Person ohne jeden Rang über Person kann Eigentümerin einer Institution sein.',
  flowGateVerified: 'Bestätigung entscheidet, ob die Gestaltung überhaupt offen ist. Eine unbeanspruchte Institution behält das neutrale Aussehen — auch beim Anzeigen, nicht nur beim Speichern.',
  flowRanksTitle: 'Ränge sind eine Leiter und sonst nichts',
  flowRanksNote: 'Was eine Institution öffnet, ist Mitgliedschaft, nicht der Rang. Der Rang trug bis zur Umstellung auch den Kontotyp — deshalb wurde beim Freigeben eines Antrags kurzzeitig der Mensch selbst zur Institution.',
  // The category each box belongs to, printed inside it. Institution, personal
  // account, permission and rank were four different things wearing one box,
  // and a diagram about who may edit what cannot afford that.
  flowKindOutside: 'außerhalb',
  flowKindData: 'Daten',
  flowKindInstitution: 'Institution',
  flowKindPerson: 'Personen-Konto',
  flowKindGrant: 'Berechtigung',
  flowKindRank: 'Rang',
  flowKindsTitle: 'Die Arten',
  flowAdminHint: 'Wie Inhalt, Institutionen und Menschen zusammenhängen. Nicht verlinkt und für Suchmaschinen gesperrt — von hier aus erreichbar, damit die Seite nicht verloren geht.',
  // The two badges on an account header. 'Institution' is the same word the
  // rank picker uses; BOT is the bot's own name shortened, and it is a badge
  // rather than a sentence because the sentence is already below it.
  badgeBot: 'BOT',
  badgeBotTitle: 'Programm von tipsBOT gepflegt, nicht von der Institution selbst',
  // Marks a line that only an administrator is being shown, so nobody mistakes
  // it for something the page says publicly.
  adminOnly: 'nur für die Administration sichtbar',
  // One word, two places: the badge on tipsBOT's own page and the machine kind
  // on the data-flow board. They are the same claim about the same account, so
  // they are the same key — two would drift the first time either is reworded.
  badgeAutomatic: 'automatisch',
};
